import { mediaRef } from '@/services/mediaAssets';

const coverImage = mediaRef('adventures/holiday-inn-little-washington-2024.png');
const heroImage = mediaRef('adventures/reckon-ball-2024-little-inn-no-text.png');
const privateEventDoorImage = mediaRef('adventures/reckon-ball-2024-private-event-door.jpg');
const parsonageFireplaceImage = mediaRef('adventures/reckon-ball-2024-parsonage-fireplace.jpg');
const giftExchangeUnboxingImage = mediaRef('adventures/reckon-ball-2024-gift-exchange-unboxing.jpg');
const cocktailHourImage = mediaRef('adventures/reckon-ball-2024-cocktail-hour.jpg');
const innMenuCoverImage = mediaRef('adventures/reckon-ball-2024-inn-menu-cover.jpg');
const giftExchangeTinselImage = mediaRef('adventures/reckon-ball-2024-gift-exchange-tinsel.jpg');
const giftExchangeFireplaceImage = mediaRef('adventures/reckon-ball-2024-gift-exchange-fireplace.jpg');
const keepsakeMenuImage = mediaRef('adventures/reckon-ball-2024-keepsake-menu.jpg');
const giftExchangeReactionImage = mediaRef('adventures/reckon-ball-2024-gift-exchange-reaction.jpg');
const parsonageGatheringImage = mediaRef('adventures/reckon-ball-2024-parsonage-gathering.jpg');
const giftExchangeHolidayHatImage = mediaRef('adventures/reckon-ball-2024-gift-exchange-holiday-hat.jpg');
const privateDinnerGroupImage = mediaRef('adventures/reckon-ball-2024-private-dinner-group.jpg');
const parsonagePorchGroupImage = mediaRef('adventures/reckon-ball-2024-parsonage-porch-group.jpg');
const snowDayGroupImage = mediaRef('adventures/reckon-ball-2024-snow-day-group.jpg');
const giftsByTreeImage = mediaRef('adventures/reckon-ball-2024-gifts-by-tree.jpg');
const stringQuartetImage = mediaRef('adventures/reckon-ball-2024-string-quartet.jpg');

export default {
  slug: '2024',
  year: 2024,
  title: 'Reckon Ball at Little Washington',
  theme: 'The Getaway',
  coverImage,
  location: { city: 'Washington', state: 'Virginia' },
  hero: {
    eyebrow: 'Reckon Ball 2024',
    title: 'The Getaway',
    subtitle: 'The Inn at Little Washington',
    summary: 'By its third year, Reckon Ball had become a destination experience. The team and their guests traveled through the snowy Virginia countryside for a weekend of hospitality, history, exceptional dining, and time together.',
    image: heroImage,
    imageAlt: 'The Reckon Crew arriving at The Inn at Little Washington during a snowy Reckon Ball 2024',
  },
  hotel: {
    name: 'The Inn at Little Washington',
    location: 'Washington, Virginia',
    overnightStay: true,
    summary: 'The Inn provided the setting for a full destination weekend, from afternoon tea and historic guest houses to private dining and brunch the next morning.',
    highlights: ['Historic guest houses', 'Afternoon tea', 'Destination weekend'],
    brunch: { venue: "Patty O's Cafe", summary: 'The group gathered for brunch before boarding the bus home.' },
  },
  timeline: [
    { title: 'Bus to Little Washington', description: 'The group traveled together through snowy roads, nearly getting stuck more than once.' },
    { title: 'Check-In', location: 'The Inn at Little Washington' },
    { title: 'Afternoon Tea', location: 'The Inn at Little Washington' },
    { title: 'Cocktails and Gift Exchange', location: 'Parsonage House Living Room' },
    { title: 'Private Dinner', location: 'Colonial Ballroom' },
    { title: 'After-Party', location: "Patty O's Cafe" },
    { title: 'Farewell Brunch', day: 'The Next Morning', location: "Patty O's Cafe" },
    { title: 'Bus Ride Home', day: 'The Next Morning' },
  ],
  experiences: [
    {
      id: 'snowy-bus', eyebrow: 'The Journey', title: 'A Snowy Ride to Little Washington', summary: 'The group traveled together through snowy roads, nearly getting stuck more than once.',
    },
    {
      id: 'afternoon-tea', eyebrow: 'Arrival', title: 'Afternoon Tea', venue: 'The Inn at Little Washington', summary: 'Afternoon tea welcomed the crew and their guests into the destination weekend.',
    },
    {
      id: 'gift-exchange', eyebrow: 'Annual Tradition', title: 'Cocktails and Gift Exchange', venue: 'Parsonage House Living Room', summary: 'The annual exchange continued over cocktails in the Parsonage House living room.',
    },
    {
      id: 'after-party', eyebrow: 'After Hours', title: 'After-Party', venue: "Patty O's Cafe", summary: "The evening continued at Patty O's after the private dinner.",
    },
    {
      id: 'brunch', eyebrow: 'The Next Morning', title: 'Farewell Brunch', venue: "Patty O's Cafe", summary: 'The group gathered for brunch before boarding the bus home.',
    },
  ],
  traditions: [
    { type: 'gift-exchange', title: 'Gift Exchange', firstYear: 2022 },
    { type: 'overnight-stay', title: 'Overnight Stay', firstYear: 2022 },
    { type: 'farewell-brunch', title: 'Farewell Brunch', firstYear: 2024 },
  ],
  dining: {
    restaurant: 'The Inn at Little Washington',
    room: 'Colonial Ballroom',
    location: 'Washington, Virginia',
    privateDining: true,
    exclusiveSpace: 'Private use of the Colonial Ballroom',
    summary: 'Dinner was served in the historic Colonial Ballroom, where George Washington is said to have once danced.',
    atmosphere: 'A live string quartet performed throughout dinner.',
    entertainment: { type: 'String Quartet', description: 'A live string quartet provided the soundtrack for the private dinner.' },
    menu: {
      eventTitle: 'Reckon Ball at Little Washington',
      year: 2024,
      restaurant: 'The Inn at Little Washington',
      room: 'Colonial Ballroom',
      location: 'Washington, Virginia',
      sections: [
        {
          title: 'Passed Canapes',
          serviceStyle: 'passed',
          items: [
            { name: 'Barbeque Duck Turnovers', dietaryNote: 'Vegetarian version available' },
            { name: 'Potato Cannoli', description: 'Creamed pimento cheese' },
            { name: 'Maine Lobster', description: 'Poached in citrus butter' },
          ],
        },
        { title: 'Before Dinner', items: [{ name: 'Truffle Popcorn' }] },
        { title: 'Opening Course', items: [{ name: 'Seasonal Soup', description: 'Served with gougere' }] },
        { title: 'First Course', items: [{ name: 'Carpaccio of Herb-Crusted Baby Lamb Loin', description: 'Caesar salad ice cream' }] },
        { title: 'Second Course', items: [{ name: 'Truffle-Stuffed Breast of Pheasant', description: 'Savoy cabbage braised in Champagne' }] },
        { title: 'Main Course', items: [{ name: 'Braised Beef Short Rib Bourguignon', description: 'Parmesan polenta' }] },
        { title: 'Dessert', items: [{ name: 'Warm Apple Tart', description: 'Buttermilk ice cream' }] },
        { title: 'A Sweet Send-Off', items: [{ name: 'Cookie Boxes' }] },
      ],
    },
  },
  signatureExperience: {
    eyebrow: 'The Defining Moment',
    title: 'Dinner with a String Quartet',
    summary: 'A live string quartet performed throughout dinner in the historic Colonial Ballroom.',
  },
  memories: [],
  gallery: [
    {
      src: privateEventDoorImage,
      alt: 'Closed for private event sign on the Parsonage door during Reckon Ball 2024',
      caption: 'The Parsonage was closed for a private Reckon Ball gathering.',
    },
    {
      src: parsonageFireplaceImage,
      alt: 'Holiday garland and a lit fireplace inside the Parsonage',
      caption: 'A warm welcome by the decorated fireplace at the Parsonage.',
    },
    {
      src: cocktailHourImage,
      alt: 'Three Reckon Ball guests holding drinks during cocktail hour',
      caption: 'Cocktail hour in the Parsonage living room.',
    },
    {
      src: giftsByTreeImage,
      alt: 'Wrapped gift-exchange presents gathered beside the Christmas tree',
      caption: 'The gift exchange waiting beneath the Christmas tree.',
    },
    {
      src: giftExchangeUnboxingImage,
      alt: 'A Reckon Ball guest opening a large gift during the holiday exchange',
      caption: 'The annual gift exchange gets underway.',
    },
    {
      src: giftExchangeTinselImage,
      alt: 'A guest wearing green tinsel reaches for a present during the gift exchange',
      caption: 'Holiday spirit during the gift exchange.',
    },
    {
      src: giftExchangeFireplaceImage,
      alt: 'Two guests examining a gift beside the Parsonage fireplace',
      caption: 'A closer look at one of the gift-exchange surprises.',
    },
    {
      src: giftExchangeReactionImage,
      alt: 'Two guests sharing a gift-exchange reaction beside the fireplace',
      caption: 'Gift exchange reactions by the fire.',
    },
    {
      src: parsonageGatheringImage,
      alt: 'Reckon Ball guests gathered around the Parsonage living room for the gift exchange',
      caption: 'The crew and guests gathered around the Parsonage gift exchange.',
    },
    {
      src: giftExchangeHolidayHatImage,
      alt: 'A smiling guest wearing a holiday headband opens a wrapped present',
      caption: 'Another gift, another Reckon Ball story.',
    },
    {
      src: innMenuCoverImage,
      alt: 'The Inn at Little Washington menu cover',
      caption: 'The Inn at Little Washington set the stage for the evening.',
    },
    {
      src: keepsakeMenuImage,
      alt: 'The personalized Reckon Ball 2024 dinner menu at The Inn at Little Washington',
      caption: 'The keepsake menu from the private dinner.',
    },
    {
      src: stringQuartetImage,
      alt: 'A string quartet performing beside the Christmas tree during dinner',
      caption: 'A string quartet brought the private dinner to life.',
    },
    {
      src: privateDinnerGroupImage,
      alt: 'The Reckon Ball 2024 group gathered around the private dining table',
      caption: 'The full table after the private dinner at The Inn at Little Washington.',
    },
    {
      src: parsonagePorchGroupImage,
      alt: 'The Reckon crew and guests posing outside the Parsonage at night',
      caption: 'The Reckon crew and guests outside the Parsonage.',
    },
    {
      src: snowDayGroupImage,
      alt: 'Four Reckon Ball guests standing outside as snow falls',
      caption: 'Snow arrived in time for a Reckon Ball group photo.',
    },
  ],
  relatedYears: [2022, 2023, 2025],
  tags: ['reckon-ball', 'destination-weekend', 'string-quartet', 'farewell-brunch'],
};
